import type { CatalogBreadcrumb, CatalogCategory, ProductDetailResponse } from "@/types/api";

/**
 * Depth cap applied while normalizing. The catalog itself has no fixed depth, but a
 * malformed or cyclic backend response must not hang the render.
 */
export const MAX_CATALOG_DEPTH = 8;

/**
 * A catalog node after normalization: `children` is always an array, so consumers
 * never have to deal with the wire type's two optional child fields.
 */
export interface CatalogNode {
  id: string;
  name: string;
  icon?: string;
  image?: string;
  /** Products directly in this node, excluding descendants. */
  productCount: number;
  /** Products in this node and all descendants; rolled up locally when the wire omits it. */
  totalProductCount: number;
  children: CatalogNode[];
}

/** Ancestor chain root→node, inclusive of the node itself. */
export type CatalogPath = CatalogNode[];

function normalizeNode(raw: CatalogCategory, depth: number): CatalogNode {
  // `subcategories` is the deprecated two-level alias — reading it keeps the recursive
  // UI working against a backend that has not shipped `children` yet.
  const rawChildren = raw.children ?? raw.subcategories ?? [];
  const children =
    depth >= MAX_CATALOG_DEPTH ? [] : rawChildren.map((child) => normalizeNode(child, depth + 1));

  const productCount = raw.productCount ?? 0;
  const rolledUp = productCount + children.reduce((sum, child) => sum + child.totalProductCount, 0);

  return {
    id: raw.id,
    name: raw.name,
    icon: raw.icon,
    image: raw.image,
    productCount,
    totalProductCount: raw.totalProductCount ?? rolledUp,
    children,
  };
}

export function normalizeCatalogTree(
  categories: readonly CatalogCategory[] | undefined,
): CatalogNode[] {
  return (categories ?? []).map((category) => normalizeNode(category, 1));
}

/**
 * Product breadcrumbs as a uniform chain. Prefers the server-supplied `breadcrumbs`,
 * falling back to the deprecated category/subcategory pair.
 */
export function normalizeProductBreadcrumbs(
  meta: ProductDetailResponse["meta"] | null | undefined,
): CatalogBreadcrumb[] {
  if (!meta) return [];
  if (meta.breadcrumbs?.length) return meta.breadcrumbs;

  const chain: CatalogBreadcrumb[] = [];
  if (meta.categoryId && meta.categoryName) {
    chain.push({ id: meta.categoryId, name: meta.categoryName });
  }
  if (meta.subcategoryId && meta.subcategoryName) {
    chain.push({ id: meta.subcategoryId, name: meta.subcategoryName });
  }
  return chain;
}
